import type { CSSProperties } from 'react';
import type { TileState } from '@lexicon/core';
import { REVEAL_STAGGER_MS } from '../game/useGame';

/** Evaluated states plus the three pre-reveal looks. */
export type TileVisualState = TileState | 'empty' | 'filled' | 'hint';

export interface TileProps {
  letter: string;
  state: TileVisualState;
  /** 1-based, for the accessible name. */
  row: number;
  /** 1-based, for the accessible name and the animation stagger. */
  col: number;
  revealing?: boolean;
  popping?: boolean;
  dancing?: boolean;
}

const STATE_CLASS: Record<TileVisualState, string> = {
  empty: 'tile--empty',
  filled: 'tile--filled',
  hint: 'tile--hint',
  absent: 'tile--absent',
  present: 'tile--present',
  correct: 'tile--correct',
};

const DANCE_STAGGER_MS = 100;

export function Tile({
  letter, state, row, col, revealing = false, popping = false, dancing = false,
}: TileProps) {
  const label = letter
    ? `row ${row}, letter ${col}: ${letter.toUpperCase()}, ${state}`
    : `row ${row}, letter ${col}: empty`;

  const className = [
    'tile',
    STATE_CLASS[state],
    revealing ? 'tile--revealing' : '',
    popping ? 'tile--pop' : '',
    dancing ? 'tile--dance' : '',
  ].filter(Boolean).join(' ');

  const style = {
    '--reveal-delay': `${(col - 1) * REVEAL_STAGGER_MS}ms`,
    '--dance-delay': `${(col - 1) * DANCE_STAGGER_MS}ms`,
  } as CSSProperties;

  return (
    <div role="img" aria-label={label} data-state={state} className={className} style={style}>
      <span aria-hidden="true">{letter.toUpperCase()}</span>
    </div>
  );
}
